import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicPageAPI } from '../../lib/api';
import {
  Phone, Mail, MapPin, Globe,
  ExternalLink, Loader2, ChevronRight, X, ChevronLeft,
  Send, Clock, Copy, Check, Navigation
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getContactLink(type, value) {
  if (!value) return null;
  switch (type) {
    case 'telegram':
      return value.startsWith('http') ? value : `https://t.me/${value.replace('@', '')}`;
    case 'phone':
      return `tel:${value}`;
    case 'email':
      return `mailto:${value}`;
    case 'instagram':
      return value.startsWith('http') ? value : `https://instagram.com/${value.replace('@', '')}`;
    case 'vk':
      return value.startsWith('http') ? value : `https://vk.com/${value}`;
    case 'ok':
      return value.startsWith('http') ? value : `https://ok.ru/profile/${value}`;
    default:
      return null;
  }
}

const socialIcons = {
  telegram: (cls) => (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  ),
  instagram: (cls) => (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 1 1-2.88 0 1.441 1.441 0 0 1 2.88 0z"/>
    </svg>
  ),
  vk: (cls) => (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.12-5.339-3.202-2.17-3.042-2.763-5.32-2.763-5.785 0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.27-1.422 2.18-3.61 2.18-3.61.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
    </svg>
  ),
  ok: (cls) => (
    <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.505 17.44a11.97 11.97 0 0 0 3.422-1.58.944.944 0 0 0-.534-1.744.944.944 0 0 0-.534.157 10.074 10.074 0 0 1-4.859 1.673 10.074 10.074 0 0 1-4.859-1.673.946.946 0 0 0-1.314.256.946.946 0 0 0 .246 1.331 11.97 11.97 0 0 0 3.422 1.58L6.78 20.29a.944.944 0 0 0 0 1.334.944.944 0 0 0 1.334 0L12 17.738l3.886 3.886a.944.944 0 0 0 1.334 0 .944.944 0 0 0 0-1.334l-2.715-2.85zM12 12.32a5.16 5.16 0 1 0 0-10.32 5.16 5.16 0 0 0 0 10.32zm0-8.432a3.272 3.272 0 1 1 0 6.544 3.272 3.272 0 0 1 0-6.544z"/>
    </svg>
  ),
};

function ContactIcon({ type, className }) {
  const lucideMap = { phone: Phone, email: Mail, address: MapPin };
  if (socialIcons[type]) return socialIcons[type](className);
  const Icon = lucideMap[type] || Globe;
  return <Icon className={className} />;
}

/* ─── Carousel ─── */
function PortfolioCarousel({ photos }) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const trackRef = useRef(null);
  const autoRef = useRef(null);
  const total = photos.length;

  const go = useCallback((idx) => { setCurrent(((idx % total) + total) % total); }, [total]);
  const next = useCallback(() => go(current + 1), [current, go]);
  const prev = useCallback(() => go(current - 1), [current, go]);

  useEffect(() => { autoRef.current = setInterval(next, 4500); return () => clearInterval(autoRef.current); }, [next]);
  const pauseAuto = () => clearInterval(autoRef.current);
  const resumeAuto = () => { clearInterval(autoRef.current); autoRef.current = setInterval(next, 4500); };

  const onTouchStart = (e) => { setTouchStart(e.touches[0].clientX); pauseAuto(); };
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    setTouchStart(null);
    resumeAuto();
  };

  useEffect(() => {
    if (lightbox === null) return;
    const update = (e) => {
      if (e.key === 'ArrowRight') setLightbox(p => ((p + 1) % total + total) % total);
      if (e.key === 'ArrowLeft') setLightbox(p => ((p - 1) % total + total) % total);
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', update);
    return () => window.removeEventListener('keydown', update);
  }, [lightbox, total]);

  if (total === 0) return null;

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl group shadow-sm" onMouseEnter={pauseAuto} onMouseLeave={resumeAuto} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div ref={trackRef} className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${current * 100}%)` }}>
          {photos.map((photo, i) => (
            <div key={i} className="w-full shrink-0">
              <div className="relative aspect-[4/3] cursor-pointer" onClick={() => setLightbox(i)}>
                <img src={`${BACKEND_URL}${photo.url}`} alt={`Работа ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
              </div>
            </div>
          ))}
        </div>
        {total > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
              <ChevronLeft className="h-5 w-5 text-stone-700" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
              <ChevronRight className="h-5 w-5 text-stone-700" />
            </button>
          </>
        )}
        {total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button key={i} onClick={() => go(i)} className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-orange-500 shadow-md' : 'w-2 bg-white/50 hover:bg-white/80'}`} />
            ))}
          </div>
        )}
      </div>
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setLightbox(null)}><X className="h-6 w-6" /></button>
          {total > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2" onClick={(e) => { e.stopPropagation(); setLightbox(p => ((p - 1) % total + total) % total); }}><ChevronLeft className="h-8 w-8" /></button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2" onClick={(e) => { e.stopPropagation(); setLightbox(p => ((p + 1) % total + total) % total); }}><ChevronRight className="h-8 w-8" /></button>
            </>
          )}
          <div className="max-w-4xl max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={`${BACKEND_URL}${photos[lightbox].url}`} alt="" className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <p className="text-white/50 text-center mt-2 text-xs">{lightbox + 1} / {total}</p>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Section heading ─── */
function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-3">
      <span className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-orange-500" />
      </span>
      {children}
    </h2>
  );
}


/* ─── Copy button ─── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="h-9 w-9 rounded-xl bg-stone-50 hover:bg-orange-50 flex items-center justify-center shrink-0 transition-colors"
      title="Скопировать адрес"
    >
      {copied
        ? <Check className="h-4 w-4 text-green-500" />
        : <Copy className="h-4 w-4 text-stone-400" />
      }
    </button>
  );
}

/* ─── Main ─── */
export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try { const res = await publicPageAPI.getPublic(slug); setPage(res.data); }
      catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  /* ─── Dynamic meta tags — page looks like standalone business site ─── */
  useEffect(() => {
    if (!page) {
      if (notFound) document.title = 'Страница не найдена';
      return;
    }
    const name = page.business_name || '';
    const desc = page.description || '';
    const services = page.services || [];
    const addr = page.contacts?.address || '';
    const phone = page.contacts?.phone || '';
    const pageUrl = window.location.href;
    const imageUrl = page.avatar_url ? (page.avatar_url.startsWith('http') ? page.avatar_url : `${BACKEND_URL}${page.avatar_url}`) : '';

    // Use custom SEO fields or auto-generate
    const seoTitle = page.seo_title || name;
    const parts = [];
    if (desc) parts.push(desc);
    if (services.length > 0) parts.push('Услуги: ' + services.map(s => s.name + (s.price ? ` (${s.price})` : '')).join(', '));
    if (addr) parts.push(addr);
    if (phone) parts.push('Тел: ' + phone);
    const seoDesc = page.seo_description || parts.join('. ') || `${name} — услуги и контакты`;

    const prevTitle = document.title;
    document.title = seoTitle;

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
      return el;
    };

    const tags = [
      setMeta('name', 'description', seoDesc),
      setMeta('name', 'author', name),
      setMeta('name', 'robots', 'index, follow'),
      setMeta('property', 'og:title', seoTitle),
      setMeta('property', 'og:description', seoDesc),
      setMeta('property', 'og:url', pageUrl),
      setMeta('property', 'og:type', 'website'),
      setMeta('property', 'og:site_name', name),
      setMeta('property', 'og:locale', 'ru_RU'),
      setMeta('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary'),
      setMeta('name', 'twitter:title', seoTitle),
      setMeta('name', 'twitter:description', seoDesc),
    ];
    if (imageUrl) {
      tags.push(setMeta('property', 'og:image', imageUrl));
      tags.push(setMeta('property', 'og:image:width', '400'));
      tags.push(setMeta('property', 'og:image:height', '400'));
      tags.push(setMeta('name', 'twitter:image', imageUrl));
    }
    if (addr) {
      tags.push(setMeta('name', 'geo.placename', addr));
    }
    const keywords = [name, ...services.map(s => s.name), addr].filter(Boolean).join(', ');
    tags.push(setMeta('name', 'keywords', keywords));
    tags.push(setMeta('name', 'apple-mobile-web-app-title', seoTitle));

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', pageUrl);

    // JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": name,
      "url": pageUrl,
    };
    if (seoDesc) jsonLd.description = seoDesc;
    if (imageUrl) jsonLd.image = imageUrl;
    if (phone) jsonLd.telephone = phone;
    if (page.contacts?.email) jsonLd.email = page.contacts.email;
    if (addr) jsonLd.address = { "@type": "PostalAddress", "streetAddress": addr };
    if (services.length > 0) {
      jsonLd.hasOfferCatalog = {
        "@type": "OfferCatalog",
        "name": "Услуги",
        "itemListElement": services.map(s => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": s.name, ...(s.description ? { description: s.description } : {}) },
          ...(s.price ? { price: s.price } : {}),
        }))
      };
    }
    const sameAs = [];
    if (page.contacts?.telegram) { const tg = page.contacts.telegram; sameAs.push(tg.startsWith('http') ? tg : `https://t.me/${tg.replace('@', '')}`); }
    if (page.contacts?.instagram) { const ig = page.contacts.instagram; sameAs.push(ig.startsWith('http') ? ig : `https://instagram.com/${ig.replace('@', '')}`); }
    if (page.contacts?.vk) { const vk = page.contacts.vk; sameAs.push(vk.startsWith('http') ? vk : `https://vk.com/${vk}`); }
    if (sameAs.length > 0) jsonLd.sameAs = sameAs;

    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      tags.forEach(el => { if (el && el.parentNode) el.parentNode.removeChild(el); });
      if (canonical && canonical.parentNode) canonical.parentNode.removeChild(canonical);
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  }, [page, notFound]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (notFound || !page) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-4">
      <div className="text-6xl mb-4">🔍</div>
      <h1 className="text-2xl font-bold mb-2 text-stone-900">Страница не найдена</h1>
      <p className="text-stone-500 text-center">Возможно, она была удалена или ещё не создана</p>
    </div>
  );

  const contactOrder = ['phone', 'address', 'email', 'telegram', 'instagram', 'vk', 'ok'];
  const contactEntries = Object.entries(page.contacts || {}).filter(([_, v]) => v).sort((a, b) => {
    const ia = contactOrder.indexOf(a[0]);
    const ib = contactOrder.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const ctaLink = getContactLink(page.contact_button_type, page.contacts?.[page.contact_button_type]);
  const portfolioPhotos = page.portfolio || [];
  const services = page.services || [];
  const schedule = page.schedule || [];
  const showSchedule = page.show_schedule && schedule.length > 0;

  const labels = { phone: 'Телефон', email: 'Email', telegram: 'Telegram', instagram: 'Instagram', vk: 'ВКонтакте', ok: 'Одноклассники', address: 'Адрес' };

  const hasServices = services.length > 0;
  const hasSchedule = showSchedule;
  const hasPortfolio = portfolioPhotos.length > 0;
  const hasContacts = contactEntries.length > 0;
  const hasContent = hasServices || hasSchedule || hasPortfolio || hasContacts;
  const addressValue = page.contacts?.address;
  const mapsLink = addressValue ? `https://yandex.ru/maps/?text=${encodeURIComponent(addressValue)}` : null;

  return (
    <div className="min-h-screen bg-stone-100">
      {/* ─── Hero ─── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50/50 to-orange-50/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,146,60,0.08),transparent_50%)]" />
        <div className="relative max-w-3xl mx-auto px-5 pt-6 pb-6 lg:pt-8 lg:pb-8 flex flex-col items-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            {page.avatar_url && (
              <img src={`${BACKEND_URL}${page.avatar_url}`} alt={page.business_name} className="h-20 w-20 lg:h-24 lg:w-24 rounded-2xl object-cover shadow-lg ring-4 ring-orange-100 shrink-0" />
            )}
            <div className="text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">{page.business_name}</h1>
              {page.description && (
                <p className="mt-1.5 text-stone-500 text-sm sm:text-base leading-relaxed max-w-md">{page.description}</p>
              )}
            </div>
          </div>
          {(ctaLink || mapsLink) && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              {ctaLink && (
                <a href={ctaLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-7 py-3 lg:px-8 lg:py-3.5 rounded-full text-sm lg:text-base shadow-lg shadow-orange-500/20 transition-all active:scale-[0.98]">
                  <Send className="h-4 w-4" />
                  {page.contact_button_label || 'Связаться'}
                </a>
              )}
              {mapsLink && (
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-900 text-white font-semibold px-7 py-3 lg:px-8 lg:py-3.5 rounded-full text-sm lg:text-base shadow-lg shadow-stone-800/20 transition-all active:scale-[0.98]">
                  <Navigation className="h-4 w-4" />
                  В путь
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Content ─── */}
      {hasContent && (
        <div className="max-w-5xl mx-auto px-5 pt-6 lg:pt-14 pb-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12">

            {/* Left column: Services + Schedule */}
            <div>
              {hasServices && (
                <section className="mb-10">
                  <SectionTitle icon={ChevronRight}>Услуги</SectionTitle>
                  <div className="flex flex-col gap-3.5">
                    {services.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-4 lg:p-5 rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md hover:border-orange-200 transition-all">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-stone-800">{s.name}</div>
                          {s.description && <p className="text-sm text-stone-400 mt-0.5 leading-snug">{s.description}</p>}
                        </div>
                        {s.price && <span className="ml-4 shrink-0 text-sm font-bold text-orange-500 bg-orange-50 px-3 py-1.5 rounded-xl">{s.price}</span>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {hasSchedule && (
                <section className="mb-10">
                  <SectionTitle icon={Clock}>График работы</SectionTitle>
                  <div className="rounded-2xl bg-white border border-stone-100 shadow-sm overflow-hidden">
                    {schedule.map((day, i) => (
                      <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i < schedule.length - 1 ? 'border-b border-stone-50' : ''}`}>
                        <span className={`text-sm font-medium ${day.enabled ? 'text-stone-700' : 'text-stone-300'}`}>{day.day}</span>
                        {day.enabled
                          ? <span className="text-sm text-stone-500 font-mono">{day.from} — {day.to}</span>
                          : <span className="text-xs text-stone-300 italic">Выходной</span>
                        }
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Right column: Portfolio + Contacts */}
            <div className={`${(hasServices || hasSchedule) ? 'mt-10 lg:mt-0' : ''}`}>
              {hasPortfolio && (
                <section className="mb-10">
                  <SectionTitle icon={ChevronRight}>Портфолио</SectionTitle>
                  <PortfolioCarousel photos={portfolioPhotos} />
                </section>
              )}

              {hasContacts && (
                <section className="mb-10">
                  <SectionTitle icon={Phone}>Контакты</SectionTitle>
                  <div className="flex flex-col gap-3">
                    {contactEntries.map(([key, value]) => {
                      const link = getContactLink(key, value);
                      const isAddress = key === 'address';
                      const Wrapper = isAddress ? 'div' : 'a';
                      const wrapperProps = isAddress
                        ? {}
                        : {
                            href: link || '#',
                            target: link && !link.startsWith('tel:') && !link.startsWith('mailto:') ? '_blank' : undefined,
                            rel: 'noopener noreferrer',
                            onClick: !link ? (e) => e.preventDefault() : undefined,
                          };
                      return (
                        <Wrapper
                          key={key}
                          {...wrapperProps}
                          className="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md hover:border-orange-200 transition-all group"
                        >
                          <div className="h-10 w-10 rounded-xl bg-orange-50 hover:bg-orange-100 flex items-center justify-center shrink-0 transition-colors">
                            <ContactIcon type={key} className="h-4 w-4 text-orange-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">{labels[key] || key}</div>
                            <div className="text-sm font-semibold text-stone-700 break-words">{value}</div>
                          </div>
                          {isAddress && (
                            <div className="flex gap-1.5 shrink-0">
                              <CopyButton text={value} />
                              <a
                                href={`https://yandex.ru/maps/?text=${encodeURIComponent(value)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-9 w-9 rounded-xl bg-stone-50 hover:bg-orange-50 flex items-center justify-center transition-colors"
                                title="Открыть на Яндекс Картах"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Navigation className="h-4 w-4 text-stone-400" />
                              </a>
                            </div>
                          )}
                          {!isAddress && link && <ExternalLink className="h-4 w-4 text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />}
                        </Wrapper>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ─── Bottom CTA ─── */}
      {(ctaLink || mapsLink) && (
        <div className="text-center py-12 flex flex-wrap items-center justify-center gap-3">
          {ctaLink && (
            <a href={ctaLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3.5 lg:px-10 lg:py-4 rounded-full text-base lg:text-lg shadow-lg shadow-orange-500/20 transition-all active:scale-[0.98]">
              <Send className="h-4 w-4 lg:h-5 lg:w-5" />
              {page.contact_button_label || 'Связаться'}
            </a>
          )}
          {mapsLink && (
            <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 bg-stone-800 hover:bg-stone-900 text-white font-semibold px-8 py-3.5 lg:px-10 lg:py-4 rounded-full text-base lg:text-lg shadow-lg shadow-stone-800/20 transition-all active:scale-[0.98]">
              <Navigation className="h-4 w-4 lg:h-5 lg:w-5" />
              В путь
            </a>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="text-center pt-2 pb-8 text-xs text-stone-400">
        Создано на <a href="/" className="text-orange-500 hover:underline font-medium">Yadela</a>
      </div>
    </div>
  );
}
